import { useLazyQuery } from '@apollo/client';
import {
  AllSuperintendentId,
  bankersRound,
  ErrorRetry,
  PersonaMessages,
  StatsSpinner,
  useName,
} from '@bechtel/client-common';
import { GET_WEEKLY_MILESTONE_PROGRESS } from '@bechtel/client-gql';
import { Box, Heading, Text } from '@bechtel/gds';
import {
  CrewMember,
  DailyMilestoneProgress,
  MilestoneStatusCode,
  ProjectWeek,
  WeeklyMilestoneAnalysis,
  WeeklyMilestoneProgress,
  WeeklyMilestoneProgressInput,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { format } from 'date-fns';
import { compact, concat, filter, find, get, indexOf, last, map, minBy, nth, some, sumBy, uniq, uniqBy } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { NameType, ValueType } from 'recharts/src/component/DefaultTooltipContent';
import { ThemeContext } from 'styled-components';
import { AvatarRegularStyled, ChildContainerStyled } from '../styled';
import { ChartHeaderContainer, ErrorContainer, TooltipContainerStyled } from './styled';
import { ClaimedProgressReferenceArea, TargetIndicatorReferenceArea, WeekDayXAxis } from './utils';
export interface ChartProps {
  selectedProjectWeek: ProjectWeek;
  selectedCrewMember: CrewMember | null;
}

interface ChartContentProps {
  selectedProjectWeek: ProjectWeek;
  selectedCrewMember: CrewMember | null;
}

const ChartHeader: FC<ChartContentProps> = ({ selectedProjectWeek, selectedCrewMember }) => {
  const { getFullName } = useName();
  const { i18n } = useLingui();
  return (
    <ChartHeaderContainer>
      <Box flexDirection={{ sm: 'column' }} pl={{ sm: '500' }}>
        <Heading size="400" weight="600">
          <Trans>Claimed Progress</Trans>
        </Heading>
        <Heading size="250" weight="600" color="grey.600" style={{ marginTop: '4px' }}>
          {selectedProjectWeek
            ? concat(
                t`Week`,
                ` ${selectedProjectWeek.weekNumber} `,
                `${selectedProjectWeek.isCurrent ? t`(Current)` : ''}`
              )
            : t`No Week`}
        </Heading>
      </Box>
      <Box pr={{ sm: '500' }} style={{ marginLeft: 'auto' }}>
        <Box flexDirection={{ sm: 'column' }}>
          <Heading size="400" weight="600">
            {selectedCrewMember
              ? `${getFullName(selectedCrewMember?.firstName, selectedCrewMember?.lastName)}`
              : t`No User Selected`}
          </Heading>
          {selectedCrewMember && selectedCrewMember?.persona && (
            <Text size="300" color="grey.600" style={{ marginTop: '4px' }}>
              {i18n._(PersonaMessages[selectedCrewMember.persona])}
            </Text>
          )}
        </Box>
        <Box pl={{ sm: '300' }}>
          <AvatarRegularStyled
            hasImageUrl={selectedCrewMember && selectedCrewMember.imageUrl ? true : false}
            src={selectedCrewMember && selectedCrewMember.imageUrl ? selectedCrewMember.imageUrl : undefined}
          />
        </Box>
      </Box>
    </ChartHeaderContainer>
  );
};

const getToolTipLabelFromFieldName = (fieldName: string) => {
  if (fieldName === 'targetHours') return 'Target';
  else if (fieldName === 'milestonehours') return 'Claimed';
  else if (fieldName === 'milestoneApprovedHours') return 'Approved';
  else if (fieldName === 'cumulativeMilestoneHours') return 'Cumulative';
};

const CharToolTip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
  if (!active || !payload || payload.length == 0 || !payload[0].payload || !payload[0].payload.weekDay) return null;

  return (
    <TooltipContainerStyled>
      <Box pb={{ sm: '400' }}>
        <Heading size="400" weight="600">
          {payload[0].payload.weekDay}
        </Heading>
      </Box>
      <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }}>
        {map(payload, (chartPayload) => {
          return (
            <>
              {chartPayload && (
                <Box pb={{ sm: '300' }}>
                  <Text size="300" color="grey.600">
                    {chartPayload.name ? `${getToolTipLabelFromFieldName(chartPayload.name?.toString())}  ` : ''}
                  </Text>
                  <Text size="300" weight="600">
                    {chartPayload.value}
                  </Text>
                </Box>
              )}
            </>
          );
        })}
      </Box>
    </TooltipContainerStyled>
  );
};

const ChartContent: FC<ChartContentProps> = ({ selectedProjectWeek, selectedCrewMember }) => {
  const theme = React.useContext(ThemeContext);
  const [getWeeklyMilestoneProgress, { loading, error, data, refetch, called }] = useLazyQuery<
    { weeklyMilestoneProgress: WeeklyMilestoneProgress },
    { input: WeeklyMilestoneProgressInput }
  >(GET_WEEKLY_MILESTONE_PROGRESS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const [showHideTooltip, setShowHideTooltip] = useState(false);
  useEffect(() => {
    if (!selectedProjectWeek || !selectedProjectWeek.id || !selectedCrewMember || !selectedCrewMember.persona) return;
    const input: WeeklyMilestoneProgressInput = {
      planningWeekId: selectedProjectWeek.id,
      persona: selectedCrewMember?.persona,
      status: MilestoneStatusCode.CLAIMED,
      isAllCrewMembers: false,
    };
    if (selectedCrewMember.id === AllSuperintendentId) {
      input.isAllCrewMembers = true;
    } else if (selectedCrewMember.id) {
      input.crewMemberId = selectedCrewMember.id;
    }
    getWeeklyMilestoneProgress({
      variables: {
        input: input,
      },
    });
  }, [getWeeklyMilestoneProgress, selectedProjectWeek, selectedCrewMember]);
  let milestoneProgress: DailyMilestoneProgress[] = [];
  const milestoneProgressData = get(data, 'weeklyMilestoneProgress.milestoneProgress', []);
  milestoneProgress = milestoneProgressData;
  if (selectedCrewMember && selectedCrewMember.id === AllSuperintendentId && milestoneProgressData.length) {
    const uniqueWeekDays = compact(uniq(map(milestoneProgressData, (item) => item.weekDay)));
    milestoneProgress = map(uniqueWeekDays, (weekDayItem) => {
      const milestonehours =
        sumBy(
          filter(milestoneProgressData, (item) => item.weekDay === weekDayItem),
          'milestonehours'
        ) || 0;

      const milestoneApprovedHours =
        sumBy(
          filter(milestoneProgressData, (item) => item.weekDay === weekDayItem),
          'milestoneApprovedHours'
        ) || 0;

      const cumulativeMilestoneHours =
        sumBy(
          filter(milestoneProgressData, (item) => item.weekDay === weekDayItem),
          'cumulativeMilestoneHours'
        ) || 0;

      const targetHours =
        sumBy(
          filter(milestoneProgressData, (item) => item.weekDay === weekDayItem),
          'targetHours'
        ) || 0;

      const date = find(milestoneProgressData, (item) => item.weekDay === weekDayItem)?.date;
      const row: DailyMilestoneProgress = {
        id: `${selectedCrewMember.persona}|${weekDayItem}|${date}|progress`,
        date: date,
        weekDay: weekDayItem,
        milestonehours: bankersRound(milestonehours, 0),
        milestoneApprovedHours: bankersRound(milestoneApprovedHours, 0),
        cumulativeMilestoneHours: bankersRound(cumulativeMilestoneHours, 0),
        targetHours: bankersRound(targetHours, 0),
      };
      return row;
    });
  }

  //Note: Backend sends targetdata with all zero's
  const uniqueTargetData = uniqBy(milestoneProgress, (mp) => mp.targetHours);
  const allZeroTargetData = uniqueTargetData.length == 1 && uniqueTargetData[0].targetHours == 0;

  const weeklyMilestoneAnalysis: WeeklyMilestoneAnalysis = get(
    data,
    'weeklyMilestoneProgress.weeklyMilestoneAnalysis',
    undefined
  );

  const showCumulativeProgressLine = () => {
    return some(milestoneProgress, ({ cumulativeMilestoneHours }) => cumulativeMilestoneHours > 0);
  };

  const getLastWeekDay = () => {
    if (!milestoneProgress || milestoneProgress.length == 0) return null;
    return last(milestoneProgress)['date'];
  };

  const getClaimProgressStartDay = () => {
    if (!selectedProjectWeek || !selectedProjectWeek.id || !milestoneProgress || milestoneProgress.length == 0)
      return null;
    if (!selectedProjectWeek.isCurrent) return getLastWeekDay();
    const today = find(milestoneProgress, (mp) => mp.weekDay == format(new Date(), 'EEEE'));
    if (!today) return null;
    return today.date;
  };

  const getClaimProgressEndDay = () => {
    if (
      !selectedProjectWeek ||
      !selectedProjectWeek.id ||
      !selectedProjectWeek.isCurrent ||
      !milestoneProgress ||
      milestoneProgress.length == 0
    )
      return null;
    const today = find(milestoneProgress, (mp) => mp.weekDay == format(new Date(), 'EEEE'));
    if (!today || today.weekDay == last(milestoneProgress)['weekDay']) return null;
    return nth(milestoneProgress, indexOf(milestoneProgress, today) + 1)?.date;
  };

  const getSecondaryYAxisMax = () => {
    if (
      !weeklyMilestoneAnalysis ||
      (weeklyMilestoneAnalysis.cumulativeMilestoneHours == 0 && weeklyMilestoneAnalysis.targetHours == 0)
    )
      return null;
    if (weeklyMilestoneAnalysis.cumulativeMilestoneHours >= weeklyMilestoneAnalysis.targetHours)
      return weeklyMilestoneAnalysis.cumulativeMilestoneHours + 100;
    else return weeklyMilestoneAnalysis.targetHours + 100;
  };

  const getSecondaryYAxisMin = () => {
    if (!milestoneProgress || milestoneProgress.length == 0) return null;
    return minBy(milestoneProgress, (milestoneHours) => milestoneHours.cumulativeMilestoneHours)
      ?.cumulativeMilestoneHours;
  };

  return (
    <>
      {(!selectedProjectWeek || !selectedProjectWeek.id) && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} flexGrow={{ sm: 1 }}>
          <Trans>No project week selected</Trans>
        </Box>
      )}
      {(!selectedCrewMember || !selectedCrewMember.persona) && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} flexGrow={{ sm: 1 }}>
          <Trans>No user or persona selected</Trans>
        </Box>
      )}
      {loading && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} flexGrow={{ sm: 1 }} data-testid="loading">
          <StatsSpinner />
        </Box>
      )}
      {!loading && error && (
        <ErrorContainer>
          <ErrorRetry refetch={refetch}>
            <Trans>There was error fetching data for claimed progress chart.</Trans>
          </ErrorRetry>
        </ErrorContainer>
      )}
      {!loading &&
        !error &&
        (!data || milestoneProgress.length == 0 || allZeroTargetData) &&
        selectedCrewMember &&
        selectedCrewMember.persona &&
        called && (
          <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} flexGrow={{ sm: 1 }}>
            <Trans>No available claimed progress chart data.</Trans>
          </Box>
        )}
      {/* loading check duplicted here to support chart to adapt for dynamic screen size changes  */}
      {!loading && selectedCrewMember && selectedCrewMember.persona && data && !allZeroTargetData && (
        <Box alignItems={{ sm: 'center' }} pt={{ sm: '400' }} pb={{ sm: '200' }}>
          {/* dual y-axis label moved outside as the position options available in recharts framework doesn't suit the hifi */}
          <Text color="grey.600" size="300" style={{ marginLeft: theme.spacings['800'] }}>
            <Trans>Hours (Daily)</Trans>
          </Text>
          <Text color="grey.600" size="300" style={{ marginLeft: 'auto', marginRight: theme.spacings['800'] }}>
            <Trans>Hours (Cumulative)</Trans>
          </Text>
        </Box>
      )}
      {!loading && selectedCrewMember && selectedCrewMember.persona && data && !allZeroTargetData && (
        <Box flexGrow={{ sm: 1 }} flexDirection={{ sm: 'column' }} data-testid="chart-content-container">
          <ResponsiveContainer width="99%" height="99%">
            <ComposedChart
              data={milestoneProgress}
              margin={{
                top: 8,
                bottom: 16,
                left: 16,
                right: 16,
              }}
            >
              <CartesianGrid stroke={String(theme.colors.grey['200'])} vertical={false} />
              <defs>
                <linearGradient id="targetColor" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={String(theme.colors.blue['300'])} stopOpacity={1} />
                  <stop offset="100%" stopColor="#d7e9f2" stopOpacity={1} />
                </linearGradient>
                <linearGradient id="claimedColor" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={String(theme.colors.orange['400'])} stopOpacity={1} />
                  <stop offset="100%" stopColor="#f6c68a" stopOpacity={1} />
                </linearGradient>
                <linearGradient id="approvedColor" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={String(theme.colors.green['400'])} stopOpacity={1} />
                  <stop offset="100%" stopColor="#7d9b7d" stopOpacity={1} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" axisLine={false} tickLine={false} tick={<WeekDayXAxis />} />
              <YAxis
                yAxisId="right"
                type="number"
                dataKey="cumulativeMilestoneHours"
                orientation="right"
                axisLine={false}
                tick={{ fontSize: theme.fontSizes['300'] }}
                tickLine={false}
                interval={0}
                domain={[getSecondaryYAxisMin(), getSecondaryYAxisMax()]}
              />
              <YAxis axisLine={false} tick={{ fontSize: theme.fontSizes['300'] }} tickLine={false} interval={0} />
              <Bar
                dataKey="targetHours"
                barSize={32}
                fill="url(#targetColor)"
                radius={[4, 4, 0, 0]}
                onMouseEnter={() => setShowHideTooltip(true)}
                onMouseLeave={() => setShowHideTooltip(false)}
              />
              <Bar
                dataKey="milestonehours"
                barSize={32}
                fill="url(#claimedColor)"
                radius={[4, 4, 0, 0]}
                onMouseEnter={() => setShowHideTooltip(true)}
                onMouseLeave={() => setShowHideTooltip(false)}
              />
              <Bar
                dataKey="milestoneApprovedHours"
                barSize={32}
                fill="url(#approvedColor)"
                radius={[4, 4, 0, 0]}
                onMouseEnter={() => setShowHideTooltip(true)}
                onMouseLeave={() => setShowHideTooltip(false)}
              />
              {showCumulativeProgressLine() && (
                <Line
                  type="monotone"
                  yAxisId="right"
                  dataKey="cumulativeMilestoneHours"
                  stroke={String(theme.colors.grey['500'])}
                  strokeWidth="1.5"
                  dot={false}
                />
              )}
              {showHideTooltip && <Tooltip content={<CharToolTip />} isAnimationActive={false} cursor={false} />}
              {weeklyMilestoneAnalysis &&
                weeklyMilestoneAnalysis.targetHours &&
                weeklyMilestoneAnalysis.targetHours != 0 && (
                  <ReferenceArea
                    yAxisId="right"
                    x1={getLastWeekDay()}
                    y1={weeklyMilestoneAnalysis.targetHours}
                    y2={weeklyMilestoneAnalysis.targetHours + 8} //8 is Size of the rhombus
                    shape={
                      <TargetIndicatorReferenceArea
                        targetHours={weeklyMilestoneAnalysis.targetHours}
                        yAxisMax={getSecondaryYAxisMax()}
                      />
                    }
                  />
                )}
              {weeklyMilestoneAnalysis &&
                weeklyMilestoneAnalysis.cumulativeMilestoneHours &&
                weeklyMilestoneAnalysis.cumulativeMilestoneHours != 0 && (
                  <ReferenceArea
                    yAxisId="right"
                    x1={getClaimProgressStartDay()}
                    x2={getClaimProgressEndDay()}
                    y1={weeklyMilestoneAnalysis.cumulativeMilestoneHours}
                    y2={weeklyMilestoneAnalysis.cumulativeMilestoneHours + 8} //8 is Size of the rhombus
                    shape={
                      <ClaimedProgressReferenceArea
                        cumulativeMilestoneHours={weeklyMilestoneAnalysis.cumulativeMilestoneHours}
                        yAxisMax={getSecondaryYAxisMax()}
                      />
                    }
                  />
                )}
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}
    </>
  );
};
export const Chart: FC<ChartProps> = ({ selectedProjectWeek, selectedCrewMember }) => {
  return (
    <ChildContainerStyled data-testid="chart-container">
      <ChartHeader selectedProjectWeek={selectedProjectWeek} selectedCrewMember={selectedCrewMember} />
      <ChartContent selectedProjectWeek={selectedProjectWeek} selectedCrewMember={selectedCrewMember} />
    </ChildContainerStyled>
  );
};
